class ToolContentModificationCapability {
  constructor(planStateSetters, planGetters, savePlan) {
    this.setters = planStateSetters;
    this.getters = planGetters;
    this.savePlan = savePlan;
  }

  async handleModifyToolsContent(action) {
    console.log("🔧 ToolContentModificationCapability: Tool content modification started:", action);
    let targetData = action;

    // Handle different action structures
    if (action.data) {
      targetData = action.data;
    }

    if (action.capability === "modify_tools_content" && action.data) {
      targetData = action.data;
    }

    const { 
      programIndex, 
      stage, 
      toolIdentifier, // Can be tool name, index, or ID
      modifications 
    } = targetData;

    // Validate input data
    if (programIndex === undefined || !stage || !toolIdentifier || !modifications) {
      console.error("❌ Invalid tool modification data:", { programIndex, stage, toolIdentifier, modifications });
      return { 
        success: false, 
        message: "Invalid data provided for tool modification",
        error: true
      };
    }

    // Validate program index exists
    const currentPrograms = this.getters.finalProgramSequence || [];
    if (programIndex >= currentPrograms.length) {
      console.error("❌ Program index out of bounds:", { programIndex, totalPrograms: currentPrograms.length });
      return {
        success: false,
        message: `Program index ${programIndex} doesn't exist. Only ${currentPrograms.length} programs available.`,
        error: true
      };
    }

    console.log(`🔧 BEFORE MODIFICATION: Modifying tool "${toolIdentifier}" in Program[${programIndex}].${stage}`);
    console.log(`🔧 Modifications:`, modifications);

    // Helper function to modify tool in a program
    const modifyToolInProgram = (program) => {
      console.log(`🔧 Processing program: ${program?.program_of_work}`);

      if (!program?.toolsByStage?.[stage]?.tools) {
        console.warn(`🔧 Stage ${stage} not found in program ${program?.program_of_work}`);
        return program;
      }

      // Create deep copy to avoid mutations
      const updatedProgram = { ...program };
      updatedProgram.toolsByStage = { ...program.toolsByStage };
      updatedProgram.toolsByStage[stage] = { ...updatedProgram.toolsByStage[stage] };
      updatedProgram.toolsByStage[stage].tools = [...updatedProgram.toolsByStage[stage].tools];

      // Find the tool to modify
      let toolIndex = -1;
      if (typeof toolIdentifier === 'number') {
        toolIndex = toolIdentifier;
      } else if (typeof toolIdentifier === 'string') {
        const tools = updatedProgram.toolsByStage[stage].tools;
        const lowerIdentifier = toolIdentifier.toLowerCase().trim();
        toolIndex = tools.findIndex(
          tool => tool.tool === toolIdentifier || tool.name === toolIdentifier || tool.id === toolIdentifier
        );
        if (toolIndex === -1) {
          toolIndex = tools.findIndex(
            tool => (tool.tool && tool.tool.toLowerCase() === lowerIdentifier) || 
                    (tool.name && tool.name.toLowerCase() === lowerIdentifier) ||
                    (tool.id && tool.id.toLowerCase() === lowerIdentifier)
          );
        }
        if (toolIndex === -1) {
          toolIndex = tools.findIndex(
            tool => (tool.tool && tool.tool.toLowerCase().includes(lowerIdentifier)) || 
                    (tool.name && tool.name.toLowerCase().includes(lowerIdentifier)) ||
                    (lowerIdentifier.length > 3 && tool.tool && tool.tool.toLowerCase().includes(lowerIdentifier))
          );
        }
        if (toolIndex === -1) {
          const identifierWords = lowerIdentifier.split(/[\s\-_]+/).filter(w => w.length > 2);
          if (identifierWords.length > 0) {
            let bestMatch = -1;
            let bestScore = 0;
            tools.forEach((tool, idx) => {
              const toolName = (tool.tool || tool.name || '').toLowerCase();
              const matchCount = identifierWords.filter(word => toolName.includes(word)).length;
              const score = matchCount / identifierWords.length;
              if (score > bestScore && score >= 0.5) {
                bestScore = score;
                bestMatch = idx;
              }
            });
            if (bestMatch !== -1) {
              console.log(`🔧 Fuzzy matched "${toolIdentifier}" to "${tools[bestMatch].tool}" (score: ${bestScore})`);
              toolIndex = bestMatch;
            }
          }
        }
      }

      if (toolIndex === -1 || toolIndex >= updatedProgram.toolsByStage[stage].tools.length) {
        console.error(`🔧 Tool "${toolIdentifier}" not found in stage ${stage}. Available tools:`, 
          updatedProgram.toolsByStage[stage].tools.map(t => t.tool || t.name));
        return program;
      }

      const originalTool = updatedProgram.toolsByStage[stage].tools[toolIndex];
      console.log(`🔧 Original tool:`, originalTool);

      // Apply modifications
      const modifiedTool = { ...originalTool };

      if (modifications.name !== undefined) {
        modifiedTool.tool = modifications.name;
        modifiedTool.name = modifications.name;
      }

      if (modifications.description !== undefined) {
        modifiedTool.description = modifications.description;
      }

      if (modifications.complexity !== undefined) {
        modifiedTool.complexity = modifications.complexity;
      }

      if (modifications.duration !== undefined) {
        modifiedTool.duration = modifications.duration;
      }

      if (modifications.related_stage !== undefined) {
        modifiedTool.related_stage = modifications.related_stage;
      }

      // Update the tool in the array
      updatedProgram.toolsByStage[stage].tools[toolIndex] = modifiedTool;

      console.log(`🔧 Modified tool:`, modifiedTool);

      // If stage is being changed, move the tool to the new stage
      if (modifications.related_stage && modifications.related_stage !== stage) {
        const newStage = modifications.related_stage;

        // Remove from current stage
        updatedProgram.toolsByStage[stage].tools.splice(toolIndex, 1);

        // Initialize new stage if it doesn't exist
        if (!updatedProgram.toolsByStage[newStage]) {
          updatedProgram.toolsByStage[newStage] = { tools: [], totalDuration: 0 };
        }

        // Add to new stage
        updatedProgram.toolsByStage[newStage].tools.push(modifiedTool);

        // Add stage to Stages array if not present
        if (updatedProgram.Stages && !updatedProgram.Stages.includes(newStage)) {
          updatedProgram.Stages = [...updatedProgram.Stages, newStage];
        }

        console.log(`🔧 Moved tool from ${stage} to ${newStage}`);
      }

      // Recalculate durations for affected stages
      const affectedStages = modifications.related_stage && modifications.related_stage !== stage 
        ? [stage, modifications.related_stage] 
        : [stage];

      affectedStages.forEach(stageToUpdate => {
        if (updatedProgram.toolsByStage[stageToUpdate]) {
          const oldDuration = updatedProgram.toolsByStage[stageToUpdate].totalDuration;
          updatedProgram.toolsByStage[stageToUpdate].totalDuration = 
            updatedProgram.toolsByStage[stageToUpdate].tools.reduce(
              (sum, t) => sum + (t.duration || 0), 0
            );
          console.log(`🔧 Stage ${stageToUpdate} duration: ${oldDuration} → ${updatedProgram.toolsByStage[stageToUpdate].totalDuration}`);
        }
      });

      // Recalculate program total duration
      const oldProgramDuration = updatedProgram.totalProgramLength;
      updatedProgram.totalProgramLength = Object.values(updatedProgram.toolsByStage).reduce(
        (sum, stageData) => sum + (stageData.totalDuration || 0), 0
      );

      console.log(`🔧 Program ${updatedProgram.program_of_work} duration: ${oldProgramDuration} → ${updatedProgram.totalProgramLength}`);

      return updatedProgram;
    };

    let updatedSequence = null;
    let newTotalDuration = 0;
    let updateSuccess = false;

    // Update finalProgramSequence
    console.log("🔧 Updating finalProgramSequence...");
    this.setters.setFinalProgramSequence(prevSequence => {
      updatedSequence = [...prevSequence];
      const oldProgram = updatedSequence[programIndex];
      updatedSequence[programIndex] = modifyToolInProgram(updatedSequence[programIndex]);

      if (updatedSequence[programIndex] !== oldProgram) {
        updateSuccess = true;
        console.log("🔧 ✅ Program object reference changed - update successful");
      }

      // Calculate total duration
      newTotalDuration = updatedSequence.reduce((sum, program) => 
        sum + (program.totalProgramLength || 0), 0
      );

      return updatedSequence;
    });

    // Update selectedPrograms
    this.setters.setSelectedPrograms(prevSelected => {
      const updatedSelected = [...prevSelected];
      if (updatedSelected[programIndex]) {
        updatedSelected[programIndex] = modifyToolInProgram(updatedSelected[programIndex]);
      }
      return updatedSelected;
    });

    // Update total duration
    this.setters.setTotalDuration(newTotalDuration);

    // Wait for React state updates to settle
    await new Promise(resolve => setTimeout(resolve, 100));

    // Mark as unsaved
    this.setters.setIsSaved(false);

    // Trigger save
    setTimeout(() => {
      if (this.savePlan && typeof this.savePlan === 'function') {
        this.savePlan();
      }
    }, 1000);

    // Return detailed result based on actual update success
    const result = {
      success: updateSuccess, // Base success on actual state update verification
      message: updateSuccess ? 
        `Successfully modified tool "${toolIdentifier}"` : 
        `Failed to modify tool "${toolIdentifier}"`,
      modifiedTool: toolIdentifier,
      modifications,
      updatedProgram: updateSuccess ? updatedSequence[programIndex] : undefined,
      stage,
      programIndex
    };

    console.log(`🔧 ${updateSuccess ? '✅' : '❌'} Tool modification ${updateSuccess ? 'complete' : 'failed'}:`, result);

    return result;
  }

  async validateModifyToolsContent(data) {
    if (data.programIndex === undefined || !data.stage || !data.toolIdentifier || !data.modifications) {
      return false;
    }

    // Ensure at least one modification is provided
    const validModifications = ['name', 'description', 'complexity', 'duration', 'related_stage'];
    const hasValidModification = validModifications.some(key => data.modifications[key] !== undefined);

    return hasValidModification;
  }
}

module.exports = ToolContentModificationCapability;